import React, { ReactNode } from "react";
import { always, maybe } from "src/utils";
import { motion, AnimatePresence } from "framer-motion";
import { k, useCss } from "kremling";
import { CpWellProps } from "./cp-well.component";

type CpWellGroupProps = {
  children: ReactNode;
  className?: string;
};

type CpWellElement = React.ReactElement<CpWellProps>;

export function CpWellGroup({
  children,
  className,
  ...rest
}: CpWellGroupProps) {
  const scope = useCss(css);

  // Convert children to array and filter out non-CpWell elements
  const wells = React.Children.toArray(children).filter(
    (child): child is CpWellElement => React.isValidElement(child),
  );

  if (wells.length === 0) {
    return null;
  }

  return (
    <div {...scope} className={always("cp-flex-col", className)} {...rest}>
      <AnimatePresence initial={false}>
        {wells.map((well, index) => {
          const isFirst = index === 0;
          const isLast = index === wells.length - 1;
          if (!well.key) {
            console.warn("CpWellGroup animation requires a key on each CpWell");
          }
          return (
            <motion.div
              key={well.key || index}
              initial={{ opacity: 0, height: 0 }}
              animate={{
                opacity: 1,
                height: "auto",
                transition: {
                  duration: 0.2,
                  ease: "easeInOut",
                },
              }}
              exit={{
                opacity: 0,
                height: 0,
                transition: {
                  duration: 0.2,
                  ease: "easeInOut",
                },
              }}
              className={always(
                "cp-well-group-item",
                maybe(isFirst && !isLast, "cp-well-group-first"),
                maybe(isLast && !isFirst, "cp-well-group-last"),
                maybe(!isFirst && !isLast, "cp-well-group-middle"),
              )}
            >
              {React.cloneElement(well, {
                className: well.props.className,
              })}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

const css = k`
  .cp-well-group {
    display: flex;
    flex-direction: column;
  }

  .cp-well-group-item {
    margin-top: -1px;
  }

  .cp-well-group-item > .cp-well-l1,
  .cp-well-group-item > .cp-well-l2,
  .cp-well-group-item > .cp-well-l3 {
    transition: border-radius 0.2s ease-in-out;
  }

  .cp-well-group-first > .cp-well-l1,
  .cp-well-group-first > .cp-well-l2,
  .cp-well-group-first > .cp-well-l3 {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  .cp-well-group-last > .cp-well-l1,
  .cp-well-group-last > .cp-well-l2,
  .cp-well-group-last > .cp-well-l3 {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }

  .cp-well-group-middle > .cp-well-l1,
  .cp-well-group-middle > .cp-well-l2,
  .cp-well-group-middle > .cp-well-l3 {
    border-radius: 0 !important;
  }
`;
